import {
    ArrowDownTrayIcon,
    BarsArrowDownIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import UserMenu from "../components/UserMenu";

function AdminMenuControl({ user }) {
    const [selectedUser] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [userMenuAccess, setUserMenuAccess] = useState({});
    const [menuAccessData, setMenuAccessData] = useState([]);
    const userRole = user?.role;
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllMenuItems();
    }, []);

    useEffect(() => {
        if(selectedUser) {
            fetchUserMenuAccess(selectedUser.id);
        }
    }, [selectedUser]);

    const fetchAllMenuItems = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/menu/section-menu`,
                { withCredentials: true },
            );
            setMenuItems(response.data);
        } catch(error) {
            console.error("Error fetching menu items:", error.message);
        }
    };

    const fetchUserMenuAccess = async (userId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/menu/${userId}`,
                { withCredentials: true },
            );
            const accessMap = response.data.reduce((acc, item) => {
                acc[item.menu_id] = item.is_enabled;
                return acc;
            }, {});
            setUserMenuAccess(accessMap);
            setMenuAccessData(response.data);
        } catch(error) {
            console.error("Error fetching user menu access:", error.message);
        }
    };

    const toggleMenuItem = async (menuId, currentStatus) => {
        try {
            const newStatus = currentStatus === 1 ? 0 : 1;
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/menu/user-menu-access/toggle`,
                {
                    menu_id: menuId,
                    // user_id: selectedUser.id,
                    is_enabled: newStatus,
                },
                { withCredentials: true },
            );
            fetchAllMenuItems();
            // setUserMenuAccess((prev) => ({ ...prev, [menuId]: newStatus }));
        } catch(error) {
            console.error("Error updating menu item access:", error.message);
        }
    };

    const handleToggle = async (menuId, currentStatus) => {
        try {
            const newStatus = currentStatus === 1 ? 0 : 1;
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/menu/user-menu-access/toggle`,
                {
                    menu_id: [menuId],
                    user_id: selectedUser?.id,
                    is_enabled: newStatus,
                },
                { withCredentials: true },
            );
            fetchUserMenuAccess(selectedUser?.id);
        } catch(error) {
            console.error("Error toggling menu access:", error.message);
        }
    };

    const handleExport = () => {
        const flattenMenuItems = (items, parentTitle = "") =>
            items.flatMap((item) => [
                {
                    id: item.id,
                    title: item.title,
                    parent: parentTitle,
                    type: item.item_type,
                    page: item.seo_page,
                    access: item.access,
                    order: item.order_index,
                    status: item.is_enabled ? "Enabled" : "Disabled",
                },
                ...flattenMenuItems(item.children || [], item.title),
            ]);

        const escapeCsvValue = (value) => {
            let text = value == null ? "" : String(value);
            if(/^[=+\-@]/.test(text)) {
                text = `'${text}`;
            }
            return `"${text.replace(/"/g, '""')}"`;
        };

        const rows = flattenMenuItems(menuItems);
        const headers = [
            "ID",
            "Title",
            "Parent",
            "Type",
            "Page",
            "Access",
            "Order",
            "Status",
        ];
        const csv = [
            headers,
            ...rows.map((item) => [
                item.id,
                item.title,
                item.parent,
                item.type,
                item.page,
                item.access,
                item.order,
                item.status,
            ]),
        ]
            .map((row) => row.map(escapeCsvValue).join(","))
            .join("\n");

        const url = URL.createObjectURL(
            new Blob([csv], { type: "text/csv;charset=utf-8;" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = "admin_menu_control.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="p-5" style={{ marginTop: "60px" }}>
            {/* Admin heading */}
            <div className="mb-4">
                <h2
                    style={{
                        color: "var(--navy)",
                        marginBottom: "20px",
                        fontSize: "large",
                        fontFamily: "large",
                    }}
                >
                    ADMIN MENU CONTROL
                </h2>
            </div>

            {/* Admin-only button group */}
            {userRole === "admin" && (
                <div className="flex justify-between items-center mb-6 px-4">
                    <h2 className="text-2xl font-bold text-gray-800"></h2>
                    
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate("/admin/create-menu")}
                            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                        >
                            <PlusIcon className="w-5 h-5 mr-1" />
                            Create Menu
                        </button>
                        <button
                            onClick={() => navigate("/admin/order-menuitems")}
                            className="flex items-center text-green-600 hover:text-green-800 font-medium"
                        >
                            <BarsArrowDownIcon className="w-5 h-5 mr-1" />
                            Order Menu
                        </button>

                        <button
                            onClick={() => navigate("/admin/delete-menu")}
                            className="flex items-center text-red-500 hover:text-red-700 font-medium"
                        >
                            <TrashIcon className="w-5 h-5 mr-1" />
                            Delete Menu
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={menuItems.length === 0}
                            className="flex items-center text-navy hover:text-navy-2 font-medium disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <ArrowDownTrayIcon className="w-5 h-5 mr-1" />
                            Export
                        </button>
                    </div>
                </div>
            )}

            {/* User Menu Tree */}
            {UserMenu({
                selectedUser: null,
                menuItems,
                userMenuAccess,
                toggleMenuItem,
            })}

            {/* Toggle Component for Audit Information */}
            <div className="p-6">
                <h2 className="text-2xl font-semibold mb-4"></h2>
                {menuAccessData.length === 0 ? (
                    <div className="text-gray-500"></div>
                ) : (
                    <ul className="space-y-4">
                        {menuAccessData.map((item) => (
                            <li
                                key={item.id}
                                className="bg-white shadow-md rounded p-4 border hover:shadow-lg transition"
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">
                                        {item.menu_title}
                                    </span>
                                    <button
                                        className={`px-3 py-1 text-sm font-medium rounded ${
                                            item.is_enabled
                                                ? "bg-green-500 text-white"
                                                : "bg-gray-300 text-gray-700"
                                        }`}
                                        onClick={() =>
                                            handleToggle(
                                                item.id,
                                                item.is_enabled,
                                            )
                                        }
                                    >
                                        {item.is_enabled
                                            ? "Enabled"
                                            : "Disabled"}
                                    </button>
                                </div>

                                <div className="text-xs text-gray-600 mt-2">
                                    <p>
                                        <strong>Created By:</strong>{" "}
                                        {item.audit_created_by || "N/A"}
                                    </p>
                                    <p>
                                        <strong>Created On:</strong>{" "}
                                        {item.audit_created_on || "N/A"}
                                    </p>
                                    <p>
                                        <strong>Updated By:</strong>{" "}
                                        {item.audit_updated_by || "N/A"}
                                    </p>
                                    <p>
                                        <strong>Updated On:</strong>{" "}
                                        {item.audit_updated_on || "N/A"}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Placeholder for future features */}
            <div>{/* <NestedMenu /> */}</div>
        </div>
    );
}

AdminMenuControl.propTypes = {
    user: PropTypes.shape({
        role: PropTypes.string,
    }),
};
export default AdminMenuControl;
